import React from 'react';
import { Table, Tag, Tooltip, Typography, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CheckCircleOutlined, CloseCircleOutlined,
  HistoryOutlined, MinusCircleOutlined,
} from '@ant-design/icons';
import { ReportCheckItem, STATE_TAG_COLORS, STEP_LABELS } from './reportVisuals';
import { formatDateTimeCompact } from '../utils/datetime';

const { Text } = Typography;

// The per-item breakdown of a report: one line per network IP or proxy
// endpoint, with the step cascade and its per-target attempts one click deeper.
//
// It is its own file because the width budget forced the split. Inside an
// expanded server row there are ~950 CSS px to spend on a 1280px screen, and
// the step column alone used to claim 300 of them while the status timeline
// stacked four tags into a 90px-tall row. Nothing was dropped: the steps became
// a row of pass/fail marks (hover for name and latency), the timeline a hover
// summary, and both are written out in full in the nested detail panel.

/** StepMarks renders the cascade as one line of pass/fail marks. */
const StepMarks: React.FC<{ item: ReportCheckItem }> = ({ item }) => {
  const { token } = theme.useToken();
  const steps = item.steps || [];
  if (steps.length === 0) return <Text type="secondary">-</Text>;
  const targets = steps.reduce((n, s) => n + (s.targetResults || []).length, 0);
  return (
    <span style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
      {steps.map((s) => (
        <Tooltip
          key={s.name}
          title={`${STEP_LABELS[s.name] || s.name}${s.latency > 0 ? `: ${s.latency.toFixed(0)}ms` : ''}${s.error ? ` — ${s.error}` : ''}`}
        >
          {s.success
            ? <CheckCircleOutlined style={{ color: token.colorSuccess, marginRight: 3 }} />
            : <CloseCircleOutlined style={{ color: token.colorError, marginRight: 3 }} />}
        </Tooltip>
      ))}
      {item.totalDuration > 0 && (
        <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>{item.totalDuration.toFixed(0)}ms</Text>
      )}
      {targets > 0 && (
        <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>· {targets} targets</Text>
      )}
    </span>
  );
};

/** timelineSummary is the escalation chain as tooltip text. */
function timelineSummary(item: ReportCheckItem): React.ReactNode {
  const tl = item.statusTimeline || [];
  if (tl.length === 0) return null;
  // The list holds escalations only, so its last entry is the worst the target
  // reached — not necessarily the state this report caught it in. Without the
  // closing line the summary silently contradicts the State tag beside it.
  const tail = tl[tl.length - 1].state;
  return (
    <div style={{ fontSize: 12 }}>
      {tl.map((c, i) => (
        <div key={`${c.state}-${c.at}-${i}`}>{c.state} · {formatDateTimeCompact(c.at)}</div>
      ))}
      {tail !== item.state && <div>then {item.state} at report time</div>}
    </div>
  );
}

/** ItemDetail is the expanded panel: full timeline, every step, every target. */
const ItemDetail: React.FC<{ item: ReportCheckItem }> = ({ item }) => {
  const { token } = theme.useToken();
  const tl = item.statusTimeline || [];
  const tail = tl.length > 0 ? tl[tl.length - 1].state : '';
  return (
    <div style={{ padding: '2px 0 2px 8px' }}>
      {tl.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, marginBottom: 6 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>How it got here:</Text>
          {tl.map((c, i) => (
            <span key={`${c.state}-${c.at}-${i}`} style={{ whiteSpace: 'nowrap' }}>
              <Tag color={STATE_TAG_COLORS[c.state] || 'default'} style={{ margin: 0, fontSize: 12, lineHeight: '18px', padding: '0 4px' }}>
                {c.state}
              </Tag>
              <Tooltip title={`${new Date(c.at).toISOString().replace('T', ' ').slice(0, 19)} UTC`}>
                <Text type="secondary" style={{ fontSize: 12, marginLeft: 3 }}>{formatDateTimeCompact(c.at)}</Text>
              </Tooltip>
              {i < tl.length - 1 && <Text type="secondary" style={{ fontSize: 12 }}> →</Text>}
            </span>
          ))}
          {tail !== item.state && (
            <Text type="secondary" style={{ fontSize: 12 }}>→ then {item.state} at report time</Text>
          )}
        </div>
      )}
      <div style={{ fontSize: 12, lineHeight: '18px' }}>
        {(item.steps || []).map((s) => (
          <div key={s.name}>
            <span>
              {s.success
                ? <CheckCircleOutlined style={{ color: token.colorSuccess, marginRight: 4 }} />
                : <CloseCircleOutlined style={{ color: token.colorError, marginRight: 4 }} />}
              {STEP_LABELS[s.name] || s.name}: {s.latency > 0 ? `${s.latency.toFixed(0)}ms` : '--'}
              {s.error && <Text type="danger" style={{ fontSize: 12 }}> — {s.error}</Text>}
            </span>
            {(s.targetResults || []).map((tr) => (
              <div key={tr.url} style={{ paddingLeft: 18, fontSize: 12, lineHeight: '18px' }}>
                {/* A skipped target was never attempted through this proxy — the worker's own
                    direct probe has that URL down. Showing it with a red cross would blame the
                    proxy for a target we gave up on. */}
                {tr.skipped
                  ? <MinusCircleOutlined style={{ color: token.colorTextQuaternary, marginRight: 4, fontSize: 12 }} />
                  : tr.success
                    ? <CheckCircleOutlined style={{ color: token.colorSuccess, marginRight: 4, fontSize: 12 }} />
                    : <CloseCircleOutlined style={{ color: token.colorError, marginRight: 4, fontSize: 12 }} />}
                <Text code style={{ fontSize: 12 }} delete={tr.skipped}>{tr.url}</Text>
                {!tr.skipped && tr.latency > 0 && <Text type="secondary" style={{ fontSize: 12 }}> {tr.latency.toFixed(0)}ms</Text>}
                {tr.error && (
                  <Tooltip title={tr.error}>
                    <Text type={tr.skipped ? 'secondary' : 'danger'} style={{ fontSize: 12 }}>
                      {' '}{tr.error.length > 60 ? `${tr.error.slice(0, 60)}…` : tr.error}
                    </Text>
                  </Tooltip>
                )}
              </div>
            ))}
          </div>
        ))}
        {item.error && (
          <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>{item.error}</Text>
        )}
      </div>
    </div>
  );
};

const ReportItemsTable: React.FC<{ items: ReportCheckItem[] }> = ({ items }) => {
  const columns: ColumnsType<ReportCheckItem> = [
    {
      // A proxy row is identified by host:port — one host usually carries
      // several endpoints — so the protocol and modem name stay next to it
      // instead of leaving the operator to guess which one failed.
      title: 'IP / Proxy', dataIndex: 'ip', key: 'ip', ellipsis: { showTitle: false },
      render: (ip: string, d: ReportCheckItem) => (
        <Tooltip title={[d.protocol, ip, d.label].filter(Boolean).join(' · ')}>
          <span style={{ whiteSpace: 'nowrap' }}>
            {d.type === 'proxy' && d.protocol && (
              <Tag style={{ margin: '0 4px 0 0', fontSize: 12, padding: '0 4px' }}>{d.protocol}</Tag>
            )}
            <Text code style={{ fontSize: 12 }}>{ip}</Text>
            {d.label && <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>{d.label}</Text>}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'State', dataIndex: 'state', key: 'state', width: 108,
      render: (s: string, d: ReportCheckItem) => {
        const summary = timelineSummary(d);
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            <Tag color={STATE_TAG_COLORS[s] || 'default'} style={{ margin: 0 }}>{s}</Tag>
            {summary && (
              // The state says what the report caught; this says how the target
              // got there — and unlike the live screen, it stays readable months
              // later, when the target has recovered or been deleted.
              <Tooltip title={summary}>
                <HistoryOutlined aria-label="Status history" style={{ marginLeft: 4, cursor: 'help' }} />
              </Tooltip>
            )}
          </span>
        );
      },
    },
    {
      // No leading icon on the tag: red already says "failed", and the icon
      // cost the 16px that clipped "Proxy TCP Connect" in this column.
      title: 'Failed Step', dataIndex: 'failedStep', key: 'failedStep', width: 145,
      ellipsis: { showTitle: false },
      render: (s: string) => s
        ? (
          <Tooltip title={STEP_LABELS[s] || s}>
            <Tag color="error" style={{ margin: 0, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'middle' }}>{STEP_LABELS[s] || s}</Tag>
          </Tooltip>
        )
        : <Text type="secondary">-</Text>,
    },
    {
      title: 'Steps', key: 'steps', width: 160,
      render: (_: unknown, d: ReportCheckItem) => <StepMarks item={d} />,
    },
    {
      // The vantage point was recorded with the result. It is the difference
      // between "this proxy is down" and "this proxy is down from Frankfurt".
      title: 'From', key: 'worker', width: 110, ellipsis: { showTitle: false },
      render: (_: unknown, d: ReportCheckItem) => {
        if (!d.workerName) return <Text type="secondary">-</Text>;
        return (
          <Tooltip title={[d.workerName, d.workerIP, d.workerCountry].filter(Boolean).join(' · ')}>
            <Text style={{ fontSize: 12 }}>{d.workerName}</Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Error', dataIndex: 'error', key: 'error', ellipsis: { showTitle: false },
      render: (e: string) => e
        ? <Tooltip title={e}><Text type="danger" style={{ fontSize: 12 }}>{e}</Text></Tooltip>
        : <Text type="secondary">-</Text>,
    },
  ];

  return (
    <Table
      dataSource={items}
      rowKey={(d) => `${d.ip}-${d.type}`}
      size="small"
      pagination={false}
      columns={columns}
      expandable={{
        expandedRowRender: (d) => <ItemDetail item={d} />,
        rowExpandable: (d) => (d.steps || []).length > 0 || (d.statusTimeline || []).length > 0,
      }}
    />
  );
};

export default ReportItemsTable;
